import { useEffect, useState } from "react";
import {
  getPushPermission,
  isPushSupported,
  registerServiceWorker,
  subscribeToAdminPush,
} from "../../../utils/pushNotifications";
import { isIos, isStandaloneMode, canShowInstallBanner } from "../../../utils/pwaUtils";
import s from "./adminApp.module.css";

function dismissKey(kind) {
  return kind === "admin" ? "baysport-admin-pwa-dismiss" : "baysport-pwa-dismiss";
}

function IosInstallHint({ kind, onDismiss }) {
  return (
    <div className={s.installBanner}>
      <div>
        <strong>Добавьте на главный экран</strong>
        <p>
          {kind === "admin"
            ? "Safari → Поделиться → «На экран Домой». Так панель клуба откроется как приложение."
            : "Safari → Поделиться → «На экран Домой». Быстрый доступ к расписанию и клубу."}
        </p>
      </div>
      <div className={s.installActions}>
        <button type="button" className={s.installDismiss} onClick={onDismiss} aria-label="Закрыть">
          ×
        </button>
      </div>
    </div>
  );
}

export default function AdminInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const key = dismissKey("admin");

  useEffect(() => {
    if (!canShowInstallBanner(key)) return undefined;
    if (isIos() && !isStandaloneMode()) {
      setShowIos(true);
      return undefined;
    }
    const onInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => window.removeEventListener("beforeinstallprompt", onInstall);
  }, [key]);

  const dismiss = () => {
    localStorage.setItem(key, "1");
    setDeferred(null);
    setShowIos(false);
  };

  if (showIos) {
    return <IosInstallHint kind="admin" onDismiss={dismiss} />;
  }

  if (!deferred) return null;

  const install = async () => {
    deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  return (
    <div className={s.installBanner}>
      <div>
        <strong>Установить панель</strong>
        <p>Добавьте управление клубом на главный экран телефона</p>
      </div>
      <div className={s.installActions}>
        <button type="button" className={s.installBtn} onClick={install}>
          Установить
        </button>
        <button type="button" className={s.installDismiss} onClick={dismiss} aria-label="Закрыть">
          ×
        </button>
      </div>
    </div>
  );
}

export function AdminPushPrompt() {
  const [permission, setPermission] = useState("default");
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(
    () => localStorage.getItem("baysport-admin-push-dismiss") === "1",
  );

  useEffect(() => {
    registerServiceWorker();
    if (isPushSupported()) {
      getPushPermission().then(setPermission);
    } else {
      setPermission("unsupported");
    }
  }, []);

  if (hidden || permission === "unsupported" || permission === "granted") {
    return null;
  }

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToAdminPush();
      setPermission("granted");
      localStorage.setItem("baysport-admin-push-dismiss", "1");
    } catch {
      /* denied */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.pushBanner}>
      <span className={s.pushIcon} aria-hidden>
        🔔
      </span>
      <div className={s.pushText}>
        <strong>Уведомления для админа</strong>
        <p>Оплаты, занятия и напоминание отметить журнал</p>
      </div>
      <button type="button" className={s.pushBtn} onClick={enable} disabled={busy}>
        {busy ? "…" : "Включить"}
      </button>
      <button
        type="button"
        className={s.installDismiss}
        onClick={() => {
          setHidden(true);
          localStorage.setItem("baysport-admin-push-dismiss", "1");
        }}
        aria-label="Закрыть"
      >
        ×
      </button>
    </div>
  );
}
